import { useState } from 'react'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import { useIsMobileOrTablet } from 'hooks/useIsMobileOrTablet'
import { useScreenSizeUp } from 'hooks/useScreenSize'

export interface ResolveIsDesktopWithSsrFallbackParams {
  hasMounted: boolean
  isDesktopViewport: boolean
  isMobileOrTabletDevice: boolean
}

/**
 * Pure desktop dual-gate resolution used by useIsDesktopWithSsrFallback.
 * - Before mount: desktop when UA is not mobile/tablet.
 * - After mount: viewport >= CSS desktop breakpoint.
 */
export function resolveIsDesktopWithSsrFallback(
  params: ResolveIsDesktopWithSsrFallbackParams,
): boolean {
  const { hasMounted, isDesktopViewport, isMobileOrTabletDevice } = params
  return hasMounted ? isDesktopViewport : !isMobileOrTabletDevice
}

/**
 * Desktop gate with SSR-safe dual judgment (same pattern as OfferPageGallery /
 * MerchandiseLandingPageHero).
 *
 * Uses isomorphic layout effect so the viewport switch happens before paint on the client.
 */
export function useIsDesktopWithSsrFallback(): boolean {
  const isMobileOrTabletDevice = useIsMobileOrTablet()
  const isDesktopViewport = useScreenSizeUp('desktop')
  const [hasMounted, setHasMounted] = useState(false)

  useIsomorphicLayoutEffect(() => {
    setHasMounted(true)
  }, [])

  return resolveIsDesktopWithSsrFallback({
    hasMounted,
    isDesktopViewport,
    isMobileOrTabletDevice,
  })
}
